import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/layout/Logo";
import {
  ChevronRight, Lock, Palette, Bell, Globe,
  FileText, Shield, HelpCircle, LogOut,
  Heart, Flame, MessageSquare, Pencil,
  Users, UserCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";

export type SettingsPage = "main" | "password" | "appearance" | "notifications" | "language" | "terms" | "privacy" | "help" | "feedback" | "friends" | "profile";

interface SettingsScreenProps {
  user: {
    name: string;
    email: string;
    totalPoints: number;
    streak: number;
    sadakaDebt: number;
  };
  onLogout: () => void;
  onPaySadaka: () => void;
  onNavigate: (page: SettingsPage) => void;
  onUpdateName?: (name: string) => Promise<void>;
}

interface SettingItem {
  icon: typeof Lock;
  label: string;
  description: string;
  page: SettingsPage;
  badge?: string;
}

export function SettingsScreen({ user, onLogout, onPaySadaka, onNavigate, onUpdateName }: SettingsScreenProps) {
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user.name);

  const saveName = async () => {
    if (!onUpdateName || !nameInput.trim() || nameInput === user.name) { setEditingName(false); return; }
    await onUpdateName(nameInput.trim());
    setEditingName(false);
  };
  const customizeItems: SettingItem[] = [
    { icon: UserCircle, label: "Profil Duzenle", description: "Ad, bio ve avatar ayarlari", page: "profile" },
    { icon: Lock, label: t("change_password"), description: t("change_password_desc"), page: "password" },
    { icon: Palette, label: t("appearance"), description: t("appearance_desc"), page: "appearance" },
    { icon: Bell, label: t("notification_settings"), description: t("notification_desc"), page: "notifications" },
    { icon: Globe, label: t("language"), description: t("language_desc"), page: "language" },
  ];

  const supportItems: SettingItem[] = [
    { icon: Users, label: "Arkadaslar", description: "Arkadas yonetimi", page: "friends" },
    { icon: FileText, label: t("terms"), description: t("terms_desc"), page: "terms" },
    { icon: Shield, label: t("privacy"), description: t("privacy_desc"), page: "privacy" },
    { icon: HelpCircle, label: t("help"), description: t("help_desc"), page: "help" },
    { icon: MessageSquare, label: t("feedback"), description: t("feedback_desc"), page: "feedback" },
  ];

  return (
    <div className="pb-24 px-4 pt-6">
      <h1 className="text-xl font-bold mb-6">{t("settings")}</h1>

      {/* Profile card */}
      <Card className="mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-ehille-dark to-ehille-dark2 p-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gold/20 flex items-center justify-center text-2xl font-bold text-gold">
              {user.name.charAt(0)}
            </div>
            <div className="flex-1">
              {editingName ? (
                <div className="flex items-center gap-2">
                  <input
                    value={nameInput}
                    onChange={e => setNameInput(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && saveName()}
                    autoFocus
                    className="bg-white/10 text-white rounded-lg px-2 py-1 text-lg font-bold border border-white/20 focus:outline-none focus:ring-1 focus:ring-gold w-full"
                  />
                  <button onClick={saveName} className="text-gold text-sm font-bold shrink-0">Kaydet</button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-white">{user.name}</h2>
                  <button onClick={() => setEditingName(true)} className="text-white/40 hover:text-gold transition-colors">
                    <Pencil size={14} />
                  </button>
                </div>
              )}
              <p className="text-sm text-white/60">{user.email}</p>
            </div>
          </div>
        </div>
        <CardContent className="p-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-lg font-bold text-gold">{user.totalPoints}</p>
              <p className="text-[10px] text-muted-foreground">{t("total_points")}</p>
            </div>
            <div>
              <p className="text-lg font-bold text-ehille-orange flex items-center justify-center gap-1">
                <Flame size={16} /> {user.streak}
              </p>
              <p className="text-[10px] text-muted-foreground">{t("day_streak_label")}</p>
            </div>
            <div>
              <p className={cn(
                "text-lg font-bold",
                user.sadakaDebt > 0 ? "text-ehille-red" : "text-ehille-green",
              )}>
                {user.sadakaDebt > 0 ? `${user.sadakaDebt.toFixed(0)} TL` : t("no_debt")}
              </p>
              <p className="text-[10px] text-muted-foreground">{t("sadaka_debt")}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sadaka payment */}
      {user.sadakaDebt > 0 && (
        <Card className="mb-6 border-ehille-red/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Heart size={20} className="text-ehille-red shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold">{t("pay_sadaka")}</p>
              <p className="text-xs text-muted-foreground">{user.sadakaDebt.toFixed(0)} TL {t("pay_sadaka_desc")}</p>
            </div>
            <Button size="sm" variant="destructive" onClick={onPaySadaka}>{t("pay")}</Button>
          </CardContent>
        </Card>
      )}

      {/* Customize */}
      <div className="mb-6">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 px-1">{t("customize")}</h3>
        <Card>
          {customizeItems.map((item, i) => (
            <SettingRow key={i} item={item} isLast={i === customizeItems.length - 1} onNavigate={onNavigate} />
          ))}
        </Card>
      </div>

      {/* Help & Support */}
      <div className="mb-6">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 px-1">{t("help_support")}</h3>
        <Card>
          {supportItems.map((item, i) => (
            <SettingRow key={i} item={item} isLast={i === supportItems.length - 1} onNavigate={onNavigate} />
          ))}
        </Card>
      </div>

      {/* Logout */}
      <Button
        variant="outline"
        size="lg"
        className="w-full text-ehille-red border-ehille-red/30 hover:bg-ehille-red/10 gap-2"
        onClick={onLogout}
      >
        <LogOut size={18} /> {t("logout")}
      </Button>

      <div className="text-center mt-6">
        <Logo size={28} className="mx-auto mb-2" />
        <p className="text-[10px] text-muted-foreground">Ehille v1.0.0</p>
      </div>
    </div>
  );
}

function SettingRow({ item, isLast, onNavigate }: { item: SettingItem; isLast: boolean; onNavigate: (page: SettingsPage) => void }) {
  const Icon = item.icon;
  return (
    <button
      onClick={() => onNavigate(item.page)}
      className={cn(
        "w-full flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors text-left",
        !isLast && "border-b border-border",
      )}
    >
      <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center shrink-0">
        <Icon size={18} className="text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{item.label}</p>
        <p className="text-[11px] text-muted-foreground truncate">{item.description}</p>
      </div>
      {item.badge && <Badge variant="warning" className="shrink-0">{item.badge}</Badge>}
      <ChevronRight size={16} className="text-muted-foreground shrink-0" />
    </button>
  );
}
